import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  DayPicker,
  DayPickerSingleProps,
  DayPickerRangeProps,
  CaptionProps,
} from 'react-day-picker';
import { vi } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Custom Caption component
function CustomCaption({ displayMonth, id }: CaptionProps) {
  const months = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Date(2000, i).toLocaleString('vi', { month: 'long' })
      ),
    []
  );
  const currentYear = new Date().getFullYear();
  const years = React.useMemo(
    () => Array.from({ length: 100 }, (_, i) => currentYear - i),
    [currentYear]
  );

  const handleMonthChange = (month: number) => {
    const event = new CustomEvent('month-change', {
      detail: { month, id },
      bubbles: true,
    });
    document.dispatchEvent(event);
  };

  const handleYearChange = (year: number) => {
    const event = new CustomEvent('year-change', {
      detail: { year, id },
      bubbles: true,
    });
    document.dispatchEvent(event);
  };

  return (
    <div className="flex gap-2 items-center justify-center py-2">
      <Select
        value={displayMonth.getMonth().toString()}
        onValueChange={(value) => handleMonthChange(Number(value))}
      >
        <SelectTrigger className="w-[110px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {months.map((month, idx) => (
            <SelectItem key={month} value={idx.toString()}>
              {month.charAt(0).toUpperCase() + month.slice(1)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={displayMonth.getFullYear().toString()}
        onValueChange={(value) => handleYearChange(Number(value))}
      >
        <SelectTrigger className="w-[90px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {years.map((year) => (
            <SelectItem key={year} value={year.toString()}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  selected,
  mode = 'single',
  ...props
}: DayPickerSingleProps) {
  const [month, setMonth] = React.useState<Date>(
    selected || props.defaultMonth || new Date()
  );

  // Sync month with selected date when it changes
  React.useEffect(() => {
    if (selected) {
      setMonth(selected);
    }
  }, [selected]);

  React.useEffect(() => {
    const handleMonthChange = (e: CustomEvent) => {
      const newDate = new Date(month);
      newDate.setMonth(e.detail.month);
      setMonth(newDate);
    };

    const handleYearChange = (e: CustomEvent) => {
      const newDate = new Date(month);
      newDate.setFullYear(e.detail.year);
      setMonth(newDate);
    };

    document.addEventListener(
      'month-change',
      handleMonthChange as EventListener
    );
    document.addEventListener('year-change', handleYearChange as EventListener);

    return () => {
      document.removeEventListener(
        'month-change',
        handleMonthChange as EventListener
      );
      document.removeEventListener(
        'year-change',
        handleYearChange as EventListener
      );
    };
  }, [month]);

  return (
    <DayPicker
      month={month}
      onMonthChange={setMonth}
      selected={selected}
      mode={mode}
      locale={vi}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col gap-4',
        caption: 'flex justify-center pt-1 relative items-center w-full',
        caption_label: 'hidden',
        nav: 'flex items-center gap-1',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-x-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected])]:rounded-md',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 p-0 font-normal aria-selected:opacity-100'
        ),
        day_range_start:
          'day-range-start aria-selected:bg-primary aria-selected:text-primary-foreground',
        day_range_end:
          'day-range-end aria-selected:bg-primary aria-selected:text-primary-foreground',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn('size-4', className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn('size-4', className)} {...props} />
        ),
        Caption: CustomCaption,
      }}
      fromYear={1900}
      toYear={new Date().getFullYear()}
      {...props}
    />
  );
}

function CalendarRange({
  className,
  classNames,
  showOutsideDays = true,
  selected,
  mode = 'range',
  ...props
}: DayPickerRangeProps) {
  const [month, setMonth] = React.useState<Date>(
    selected?.from || props.defaultMonth || new Date()
  );

  // Sync month with selected date when it changes
  React.useEffect(() => {
    if (selected?.from) {
      setMonth(selected.from);
    }
  }, [selected]);

  React.useEffect(() => {
    const handleMonthChange = (e: CustomEvent) => {
      const newDate = new Date(month);
      newDate.setMonth(e.detail.month);
      setMonth(newDate);
    };

    const handleYearChange = (e: CustomEvent) => {
      const newDate = new Date(month);
      newDate.setFullYear(e.detail.year);
      setMonth(newDate);
    };

    document.addEventListener(
      'month-change',
      handleMonthChange as EventListener
    );
    document.addEventListener('year-change', handleYearChange as EventListener);

    return () => {
      document.removeEventListener(
        'month-change',
        handleMonthChange as EventListener
      );
      document.removeEventListener(
        'year-change',
        handleYearChange as EventListener
      );
    };
  }, [month]);

  return (
    <DayPicker
      month={month}
      onMonthChange={setMonth}
      selected={selected}
      mode={mode}
      locale={vi}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col gap-4',
        caption: 'flex justify-center pt-1 relative items-center w-full',
        caption_label: 'hidden',
        nav: 'flex items-center gap-1',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-x-1',
        head_row: 'flex',
        head_cell:
          'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-range-start)]:rounded-l-md [&:has([aria-selected])]:first:[&:has([aria-selected])]:rounded-l-md [&:has([aria-selected])]:last:[&:has([aria-selected])]:rounded-r-md',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 p-0 font-normal aria-selected:opacity-100'
        ),
        day_range_start:
          'day-range-start aria-selected:bg-primary aria-selected:text-primary-foreground aria-selected:rounded-l-md aria-selected:rounded-r-none',
        day_range_end:
          'day-range-end aria-selected:bg-primary aria-selected:text-primary-foreground aria-selected:rounded-r-md aria-selected:rounded-l-none',
        day_selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        day_today: 'bg-accent text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground aria-selected:rounded-none',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn('size-4', className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn('size-4', className)} {...props} />
        ),
        Caption: CustomCaption,
      }}
      fromYear={1900}
      toYear={new Date().getFullYear()}
      {...props}
    />
  );
}

export { Calendar, CalendarRange };
